import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Buildings, Tag, ArrowRight, Check, CaretDown,
  ShieldCheck, Scales, Database, Robot, Bank, Briefcase,
  UserCircle, Warning, Info, Sparkle, ChartBar, Target,
  Plus, X, Lightning, Gauge
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Theme colors for visual distinction
const THEME_COLORS = {
  "Tax Compliance": "bg-amber-500/10 text-amber-400 border-amber-500/30",
  "Employment & Labour": "bg-blue-500/10 text-blue-400 border-blue-500/30",
  "Corporate Governance": "bg-purple-500/10 text-purple-400 border-purple-500/30",
  "Data Protection": "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  "Cybersecurity": "bg-red-500/10 text-red-400 border-red-500/30",
  "Financial Crime": "bg-orange-500/10 text-orange-400 border-orange-500/30",
  "Consumer Protection": "bg-green-500/10 text-green-400 border-green-500/30",
  "General Compliance": "bg-gray-500/10 text-gray-400 border-gray-500/30",
  "Banking Regulation": "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  "Financial Services": "bg-gold/10 text-gold border-gold/30",
  "AML": "bg-rose-500/10 text-rose-400 border-rose-500/30"
};

const DepartmentWorkflowPage = () => {
  const { user } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [themeAssignments, setThemeAssignments] = useState({});
  const [userDepartment, setUserDepartment] = useState(null);
  const [myRegulations, setMyRegulations] = useState([]);
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assignments');
  const [savingAssignment, setSavingAssignment] = useState(null);

  // Get unique themes from regulations or use defaults
  const allThemes = Object.keys(THEME_COLORS);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [deptRes, assignRes, userDeptRes] = await Promise.all([
        axios.get(`${API}/departments`),
        axios.get(`${API}/departments/theme-assignments`),
        axios.get(`${API}/users/department`)
      ]);

      setDepartments(deptRes.data.departments || []);
      setThemeAssignments(assignRes.data.assignments || {});
      setUserDepartment(userDeptRes.data.department);

      // Fetch my regulations if user has department
      if (userDeptRes.data.department_id) {
        const myRegsRes = await axios.get(`${API}/departments/my-regulations`);
        setMyRegulations(myRegsRes.data.regulations || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load department data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTheme = async (theme, departmentId) => {
    setSavingAssignment(theme);
    try {
      await axios.put(`${API}/departments/theme-assignments`, {
        theme,
        department_id: departmentId
      });
      setThemeAssignments(prev => ({ ...prev, [theme]: departmentId }));
      toast.success(`${theme} assigned to department`);
    } catch (error) {
      toast.error('Failed to assign theme');
    } finally {
      setSavingAssignment(null);
    }
  };

  const handleSetMyDepartment = async (departmentId) => {
    try {
      await axios.put(`${API}/users/department?department_id=${departmentId}`);
      const dept = departments.find(d => d.id === departmentId);
      setUserDepartment(dept);
      toast.success(`Department set to ${dept?.name}`);
      
      // Refresh my regulations
      const myRegsRes = await axios.get(`${API}/departments/my-regulations`);
      setMyRegulations(myRegsRes.data.regulations || []);
    } catch (error) {
      toast.error('Failed to set department');
    }
  };

  const runGapAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/compliance/gap-analysis`);
      setGapAnalysis(response.data);
      setActiveTab('gaps');
      toast.success('Gap analysis complete');
    } catch (error) {
      toast.error('Failed to run gap analysis');
    }
  };

  const addRecommendedRegulation = async (gap) => {
    try {
      await axios.post(`${API}/compliance/add-recommendation`, null, {
        params: {
          regulation_name: gap.regulation_name,
          regulator: gap.regulator,
          theme: gap.theme
        }
      });
      toast.success(`Added ${gap.regulation_name} to your universe`);
      // Refresh gap analysis
      runGapAnalysis();
    } catch (error) {
      toast.error('Failed to add regulation');
    }
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || deptId;
  };

  const tabs = [
    { id: 'assignments', label: 'Theme Assignments', icon: Tag },
    { id: 'my-work', label: 'My Department', icon: Briefcase },
    { id: 'gaps', label: 'Gap Analysis', icon: Target }
  ];

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="text-gold font-mono">Loading workflow management...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-heading font-medium text-text-primary mb-1">
                Workflow Management
              </h1>
              <p className="text-sm text-text-secondary">
                Assign regulatory themes to departments and track compliance coverage
              </p>
            </div>
            <button
              onClick={runGapAnalysis}
              className="flex items-center gap-2 px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all"
            >
              <Sparkle size={18} />
              Run Gap Analysis
            </button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-background-surface border border-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold text-gold">
                {departments.length}
              </div>
              <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                Departments
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-4 bg-background-surface border border-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold text-gold">
                {Object.keys(themeAssignments).length}
              </div>
              <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                Themes Assigned
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-4 bg-background-surface border border-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold text-gold">
                {myRegulations.length}
              </div>
              <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                My Regulations
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-4 bg-background-surface border border-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold text-emerald-400">
                {gapAnalysis ? `${gapAnalysis.coverage_score}%` : '--'}
              </div>
              <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                Coverage Score
              </div>
            </motion.div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-6">
          <div className="flex gap-1 border-b border-border">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium transition-all flex items-center gap-2 ${
                  activeTab === tab.id 
                    ? 'text-gold border-b-2 border-gold' 
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Theme Assignments Tab */}
          {activeTab === 'assignments' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Instructions */}
              <div className="p-4 bg-gold/10 border border-gold/30 rounded-lg flex items-start gap-3">
                <Info size={20} className="text-gold flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-text-primary font-medium mb-1">Assign Themes to Departments</p>
                  <p className="text-text-secondary">
                    Each regulatory theme can be assigned to a department. Users in that department will 
                    only see regulations belonging to their assigned themes.
                  </p>
                </div>
              </div>

              {/* Theme Assignment Grid */}
              <div className="bg-background-surface border border-border rounded-lg overflow-hidden">
                <div className="grid grid-cols-[1fr,200px] gap-4 p-4 border-b border-border bg-background">
                  <div className="text-sm font-medium text-text-tertiary uppercase tracking-wider">
                    Regulatory Theme
                  </div>
                  <div className="text-sm font-medium text-text-tertiary uppercase tracking-wider">
                    Assigned Department
                  </div>
                </div>
                
                <div className="divide-y divide-border">
                  {allThemes.map((theme) => (
                    <div key={theme} className="grid grid-cols-[1fr,200px] gap-4 p-4 items-center hover:bg-background/50">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${THEME_COLORS[theme] || 'bg-gray-500/10 text-gray-400 border-gray-500/30'}`}>
                          {theme}
                        </span>
                      </div>
                      <div className="relative">
                        <select
                          value={themeAssignments[theme] || ''}
                          onChange={(e) => handleAssignTheme(theme, e.target.value)}
                          disabled={savingAssignment === theme}
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary text-sm appearance-none cursor-pointer hover:border-gold focus:border-gold focus:outline-none disabled:opacity-50"
                        >
                          <option value="">Select department...</option>
                          {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>
                              {dept.name}
                            </option>
                          ))}
                        </select>
                        <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" />
                        {savingAssignment === theme && (
                          <div className="absolute right-8 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* My Department Tab */}
          {activeTab === 'my-work' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Set My Department */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-heading font-medium text-text-primary">Your Department</h3>
                    <p className="text-sm text-text-secondary">
                      Select your department to see assigned regulations
                    </p>
                  </div>
                  {userDepartment && (
                    <span className="px-3 py-1.5 bg-gold/10 text-gold border border-gold/30 rounded-full text-sm font-medium">
                      {userDepartment.name}
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-5 gap-3">
                  {departments.map(dept => (
                    <button
                      key={dept.id}
                      onClick={() => handleSetMyDepartment(dept.id)}
                      className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                        userDepartment?.id === dept.id
                          ? 'bg-gold/10 border-gold text-gold'
                          : 'bg-background border-border text-text-secondary hover:border-gold hover:text-gold'
                      }`}
                    >
                      {dept.short_name}
                    </button>
                  ))}
                </div>
              </div>

              {/* My Assigned Regulations */}
              {userDepartment ? (
                <div className="bg-background-surface border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-heading font-medium text-text-primary">
                      My Assigned Regulations ({myRegulations.length})
                    </h3>
                  </div>
                  
                  {myRegulations.length > 0 ? (
                    <div className="space-y-3">
                      {myRegulations.map((reg, idx) => (
                        <div key={reg.id || idx} className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-text-primary truncate">
                              {reg.name}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-text-tertiary">{reg.regulator}</span>
                              <span className="text-xs text-text-tertiary">•</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${THEME_COLORS[reg.theme] || 'bg-gray-500/10 text-gray-400'}`}>
                                {reg.theme}
                              </span>
                            </div>
                          </div>
                          <div className={`px-2 py-1 rounded text-xs font-medium ${
                            reg.risk_level === 'High' ? 'bg-red-500/10 text-red-400' :
                            reg.risk_level === 'Medium' ? 'bg-amber-500/10 text-amber-400' :
                            'bg-emerald-500/10 text-emerald-400'
                          }`}>
                            {reg.risk_level} Risk
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Briefcase size={48} className="mx-auto text-text-tertiary mb-3" weight="light" />
                      <p className="text-text-tertiary">No regulations assigned to your department</p>
                      <p className="text-text-tertiary text-sm mt-1">
                        Check the Theme Assignments tab to see which themes are assigned to {userDepartment.name}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-background-surface border border-border rounded-lg p-8 text-center">
                  <UserCircle size={48} className="mx-auto text-text-tertiary mb-3" weight="light" />
                  <p className="text-text-primary font-medium mb-1">Select Your Department</p>
                  <p className="text-text-tertiary text-sm">
                    Choose your department above to see your assigned regulations
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Gap Analysis Tab */}
          {activeTab === 'gaps' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {gapAnalysis ? (
                <>
                  {/* Coverage Score */}
                  <div className="bg-background-surface border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-heading font-medium text-text-primary">Compliance Coverage</h3>
                        <p className="text-sm text-text-secondary">
                          Based on your business profile and regulatory universe
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`text-4xl font-mono font-bold ${
                          gapAnalysis.coverage_score >= 80 ? 'text-emerald-400' :
                          gapAnalysis.coverage_score >= 60 ? 'text-amber-400' :
                          'text-red-400'
                        }`}>
                          {gapAnalysis.coverage_score}%
                        </div>
                        <div className="text-xs text-text-tertiary uppercase tracking-wider">
                          Coverage Score
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="h-3 bg-background rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${gapAnalysis.coverage_score}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className={`h-full rounded-full ${
                          gapAnalysis.coverage_score >= 80 ? 'bg-emerald-500' :
                          gapAnalysis.coverage_score >= 60 ? 'bg-amber-500' :
                          'bg-red-500'
                        }`}
                      />
                    </div>
                    
                    <div className="flex justify-between mt-2 text-xs text-text-tertiary">
                      <span>{gapAnalysis.current_regulation_count} regulations in universe</span>
                      <span>{gapAnalysis.business_profile_summary?.recommended_regulation_count || 0} recommended for your profile</span>
                    </div>
                  </div>

                  {/* Recommendations */}
                  {gapAnalysis.recommendations?.length > 0 && (
                    <div className="p-4 bg-gold/10 border border-gold/30 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Sparkle size={20} className="text-gold flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-text-primary font-medium mb-2">Recommendations</p>
                          <ul className="space-y-1">
                            {gapAnalysis.recommendations.map((rec, idx) => (
                              <li key={idx} className="text-sm text-text-secondary">• {rec}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Identified Gaps */}
                  {gapAnalysis.gaps?.length > 0 && (
                    <div className="bg-background-surface border border-border rounded-lg p-6">
                      <h3 className="text-lg font-heading font-medium text-text-primary mb-4">
                        Identified Gaps ({gapAnalysis.gaps.length})
                      </h3>
                      <div className="space-y-3">
                        {gapAnalysis.gaps.map((gap, idx) => (
                          <div key={idx} className="flex items-start gap-4 p-4 bg-background border border-border rounded-lg">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              gap.risk_if_missing === 'high' ? 'bg-red-500/10' : 'bg-amber-500/10'
                            }`}>
                              <Warning size={20} className={
                                gap.risk_if_missing === 'high' ? 'text-red-400' : 'text-amber-400'
                              } weight="fill" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <div className="text-sm font-medium text-text-primary">
                                    {gap.regulation_name}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-text-tertiary">{gap.regulator}</span>
                                    <span className="text-xs text-text-tertiary">•</span>
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${THEME_COLORS[gap.theme] || 'bg-gray-500/10 text-gray-400'}`}>
                                      {gap.theme}
                                    </span>
                                  </div>
                                  <p className="text-xs text-text-tertiary mt-2">{gap.reason}</p>
                                </div>
                                <button
                                  onClick={() => addRecommendedRegulation(gap)}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-gold text-gold-foreground rounded-sm text-xs font-medium hover:bg-gold-hover transition-colors whitespace-nowrap"
                                >
                                  <Plus size={14} />
                                  Add to Universe
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {gapAnalysis.gaps?.length === 0 && (
                    <div className="bg-background-surface border border-border rounded-lg p-8 text-center">
                      <Check size={48} className="mx-auto text-emerald-400 mb-3" weight="bold" />
                      <p className="text-text-primary font-medium mb-1">No Gaps Identified</p>
                      <p className="text-text-tertiary text-sm">
                        Your regulatory universe appears comprehensive for your business profile
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-background-surface border border-border rounded-lg p-8 text-center">
                  <Target size={48} className="mx-auto text-text-tertiary mb-3" weight="light" />
                  <p className="text-text-primary font-medium mb-1">Run Gap Analysis</p>
                  <p className="text-text-tertiary text-sm mb-4">
                    Analyze your regulatory universe against your business profile to identify potential gaps
                  </p>
                  <button
                    onClick={runGapAnalysis}
                    className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all inline-flex items-center gap-2"
                  >
                    <Sparkle size={16} />
                    Run Analysis
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepartmentWorkflowPage;
